import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import PageHeader from "./-components/PageHeader";
import ConsoleTable, { DataListProps } from "./-components/ConsoleTable";
import Pagination from "components/Pagination";
import { useGetConsoleRecruitStatus } from "hooks/console/useGetConsoleRecruitStatus";
import { makeIntComma } from "hooks/utils/util";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import Seo from "components/Seo";

const theadStyle = "text-[#222222] text-[16px] text-center";

const initialSearchForm = {
    category: "morning",
    search: "contents",
    searchtxt: "",
    page: 1,
    limit: "10",
};

export default function ConsoleMorningRecruitStatus() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState(initialSearchForm);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const { data, isLoading } = useGetConsoleRecruitStatus(searchForm);
    // console.log("recruit data", data);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    return (
        <>
            <Seo type="console" pageTitle="채용지원현황" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="채용지원" title="채용지원현황" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form>
                                <div className="flex justify-end items-center gap-2">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={searchForm.search}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, search: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[120px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            {/* <SelectItem value="subject">제목</SelectItem> */}
                                            <SelectItem value="contents">내용</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchForm.searchtxt}
                                            onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value }))}
                                            placeholder="검색어를 입력해주세요."
                                            className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                        />
                                        <ReadingGlasses />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "8%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                return (
                                                    <label
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {/* <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(row.list_no)}
                                                            onChange={() => toggleRow(row.list_no)}
                                                        /> */}
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },
                                        {
                                            title: "이름/나이",
                                            width: "20%",
                                            key: "m_name",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps, rowIndex) => (
                                                <div className="flex items-center justify-center px-2">
                                                    <span>{row.m_name}</span>/<span>{row.m_age}</span>
                                                </div>
                                            ),
                                        },
                                        {
                                            title: "연락처",
                                            width: "20%",
                                            key: "m_tel",
                                            className: twMerge(theadStyle, "text-[#666666] text-wrap"),
                                        },
                                        {
                                            title: "거주지",
                                            width: "20%",
                                            key: "m_area",
                                            className: twMerge(theadStyle, "text-[#666666] text-wrap"),
                                        },
                                        {
                                            title: "차량명",
                                            width: "15%",
                                            key: "m_car",
                                            className: twMerge(theadStyle, "text-[#666666] text-wrap"),
                                        },
                                        {
                                            title: "해당업무",
                                            width: "12%",
                                            key: "m_up",
                                            className: twMerge(theadStyle, "text-[#666666] text-wrap"),
                                        },
                                        {
                                            title: "근무시간",
                                            width: "15%",
                                            key: "m_time",
                                            className: twMerge(theadStyle, "text-[#666666] text-wrap"),
                                        },
                                        {
                                            title: "내용",
                                            width: "30%",
                                            key: "contents",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps) => (
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: row?.contents || "" }}
                                                    className="text-wrap whitespace-pre"
                                                />
                                            ),
                                        },
                                        {
                                            title: "등록일",
                                            width: "15%",
                                            key: "w_date",
                                            className: twMerge(theadStyle, "text-[#999999] text-[14px]"),
                                        },
                                    ]}
                                    tbody={data?.data_list || []}
                                    className="border-t border-t-[1px] border-t-[#dddddd]"
                                    uniqueKeyName="list_no"
                                    isLoading={isLoading}
                                />
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    // setSearchForm(prev => ({ ...prev, page: newPage }))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
